import { useDocumentStore } from "@shared/components/providers/SupergrainProvider"
import {
  emptyProseMirrorContent,
  getEditorCardStackState,
  getEditorJsonContent,
  type EditorCardStackState,
} from "@shared/lib/card-stack/prose-mirror-utils"
import type { LessonStandard, UnifiedCardStack } from "@shared/models/card-stack"
import { setAnalyticsRuntime } from "@shared/unified-tiptap/analytics/instrument"
import { asHostId } from "@shared/unified-tiptap/analytics/types"
import { useLinkDialog } from "@shared/unified-tiptap/editor-ui/link/use-link-dialog"
import { cardStackExtensions } from "@shared/unified-tiptap/extensions"
import {
  clearVersionBoundaryListener,
  setVersionBoundaryListener,
} from "@shared/unified-tiptap/extensions/version-boundary/version-boundary-extension"
import type {
  CommentActions,
  DerivedPermissions,
  UnifiedTiptapUiValue,
} from "@shared/unified-tiptap/unified-tiptap-ui-context"
import UnifiedTiptapRenderer from "@shared/unified-tiptap/UnifiedTiptapRenderer"
import { setActiveEditorId } from "@shared/unified-tiptap/util/active-editor"
import type { CardAttrsBase } from "@shared/unified-tiptap/util/card"
import { findNodeAndPos } from "@shared/unified-tiptap/util/descendant-helpers"
import { handleSliceDrop, isLessonDrag } from "@shared/unified-tiptap/util/drag-and-drop"
import { assignRef } from "@shared/util/assign-ref"
import ObjectId from "@shared/util/object-id-gen"
import { tracked, useSignalEffect } from "@supergrain/kernel/react"
import type { Editor, EditorEvents } from "@tiptap/core"
import { useEditor } from "@tiptap/react"
import { lessonEditIds } from "cc-frontend/lib/actions/lesson/lesson-edit-ids"
import type Filestack from "cc-frontend/services/filestack"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type Session from "cc-frontend/services/session"
import { isEqual, throttle } from "lodash-es"
import { useCallback, useEffect, useMemo, useRef, type Ref, type RefObject } from "react"
import { useAddFromGoogleDrive } from "react-app/hooks/useAddFromGoogleDrive"
import { guardedSave } from "react-app/lib/guarded-save"
import { useEmberService } from "react-app/providers/ember-service-context"
import { reportEditorAnalyticsError, useEditorAnalyticsPort } from "./editor-analytics-port"

/**
 * A stable scheduler that runs `respond.current` on the next microtask, coalescing repeat calls
 * within the same tick into one run.
 *
 * The version-boundary signal is called synchronously from inside a ProseMirror transaction, so
 * the response must be scheduled — persistence cannot re-enter the editor transaction.
 */
function useDeferredResponse(respond: RefObject<() => void>): () => void {
  const scheduledRef = useRef(false)
  return useCallback(() => {
    if (scheduledRef.current) return
    scheduledRef.current = true
    queueMicrotask(() => {
      scheduledRef.current = false
      respond.current()
    })
  }, [respond])
}

interface Props {
  /** Must be a supergrain document, i.e., an output of useDocument. */
  cardStack: UnifiedCardStack
  modelsNeededForFirstEdit: ModelsNeededForFirstEdit
  isNonInstructional: boolean
  /**
   * Open the standards search modal.
   * When undefined, the Standards option in the AddButton is skipped.
   */
  openStandardsModal?: (startingStandards: LessonStandard[]) => Promise<Array<LessonStandard>>
  /**
   * Open the Google Classroom sidebar for the given card id.
   * When undefined, clicking a Google Classroom card does nothing.
   */
  openGoogleClassroomSidebar?: (cardId: string) => void
  /**
   * This lesson as a student sees it, for the card menu's class-website callout. Undefined
   * for a card stack that is on no day of no course (a template, a unit).
   */
  studentLessonUrl?: string
  editorRef?: Ref<Editor | null>
  /**
   * Whether the AI panel is open on this card stack. While it is, the editor is the panel's
   * canvas: it neither saves what it holds nor takes in what arrives, so the assistant's
   * unaccepted edits stay local and nothing overwrites them mid-stream. The panel writes the
   * lesson itself once the user accepts.
   */
  isAiOpen?: boolean
  /**
   * Shows the card stack as readonly, regardless of permissions.
   *
   * Also consider UnifiedLessonSimpleReadOnly, a variant of this component with simpler
   * styling and that does not react to changes.
   */
  readonly?: boolean
}

/**
 * State machine for local edits.
 */
type EditState =
  | {
      label: "focused"
      hasEdited: boolean
      /** The state when we became focused. Used for action undo. */
      originalEditorState: EditorCardStackState
      /**
       * The editor state at the last intermediate update or when we became focused.
       * Used to see what needs updating at the next intermediate update.
       */
      lastUpdateEditorState: EditorCardStackState
    }
  | { label: "unfocused" }

export default tracked(function UnifiedCardStackEditor({
  cardStack,
  modelsNeededForFirstEdit,
  isNonInstructional,
  openStandardsModal,
  openGoogleClassroomSidebar,
  studentLessonUrl,
  editorRef: propEditorRef,
  isAiOpen = false,
  readonly = false,
}: Props) {
  const lessonEditor = useEmberService<LessonEditor>("lesson-editor")
  const documentStore = useDocumentStore()

  /** Our own handle on the editor; also forwarded to the editorRef prop. */
  const editorRef = useRef<Editor | null>(null)

  const planbookId = cardStack.relationships.planbook?.data.id
  // meta can be absent entirely on not-yet-synced documents.
  const permissions = cardStack.meta?.permissions ?? []
  const canComment =
    permissions.includes("can-add-comments") &&
    cardStack.attributes.usedAs !== "lesson-template" &&
    !readonly
  const canEdit = permissions.includes("can-edit-plan") && !readonly

  const getStateFromCardStack = (): EditorCardStackState => ({
    title: isNonInstructional ? "" : (cardStack.attributes.title ?? ""),
    content: cardStack.attributes.proseMirror ?? emptyProseMirrorContent(),
  })

  const getStateFromEditor = useCallback(
    (): EditorCardStackState =>
      getEditorCardStackState(editorRef.current!, { isUntitled: isNonInstructional }),
    [isNonInstructional],
  )

  const setEditorState = useCallback(
    (state: EditorCardStackState) => {
      const editor = editorRef.current
      if (!editor) return

      editor.commands.setContent(getEditorJsonContent(state, { isUntitled: isNonInstructional }), {
        emitUpdate: false,
      })
    },
    [isNonInstructional],
  )

  // ----------
  // Writing lesson content
  // ----------

  const editStateRef = useRef<EditState>({ label: "unfocused" })

  // In a ref because the throttled writer and the read effect both run outside the render
  // that would carry a new prop value.
  const isAiOpenRef = useRef(isAiOpen)
  isAiOpenRef.current = isAiOpen

  const isInOwnActionRef = useRef(false)
  /**
   * Wrap our own editor CardStack actions in this function to avoid triggering the read path's useSignalEffect.
   */
  const wrapOwnActions = useCallback((f: () => void) => {
    if (isInOwnActionRef.current) return f()

    isInOwnActionRef.current = true
    try {
      return f()
    } finally {
      isInOwnActionRef.current = false
    }
  }, [])

  /** Callback to perform intermediate edit actions if needed. Throttled. */
  const scheduleIntermediateEdit = useMemo(
    () =>
      throttle(
        guardedSave(() => {
          if (!canEdit || isAiOpenRef.current) return

          const editState = editStateRef.current
          if (editState.label !== "focused") return

          wrapOwnActions(() => {
            const prevState = editState.lastUpdateEditorState
            const currentState = getStateFromEditor()
            if (currentState.title !== prevState.title) {
              lessonEditor.editTitle(
                modelsNeededForFirstEdit,
                cardStack.id,
                currentState.title,
                false,
              )
            }
            if (!isEqual(currentState.content.json, prevState.content.json)) {
              lessonEditor.editUnifiedLesson(
                modelsNeededForFirstEdit,
                cardStack.id,
                currentState.content,
              )
            }

            editState.lastUpdateEditorState = currentState
          })
        }),
        750,
      ),
    [canEdit],
  )

  // Cancel the throttled intermediate edit on unmount.
  useEffect(() => () => scheduleIntermediateEdit.cancel(), [scheduleIntermediateEdit])

  const doFinalEdit = (state?: EditorCardStackState) => {
    if (!canEdit || isAiOpenRef.current) return

    const editState = editStateRef.current
    const originalState =
      // If we're focused, we need to use the stored state because the CardStack has been overwritten
      // by intermediate edit actions.
      editState.label === "focused" ? editState.originalEditorState : getStateFromCardStack()
    const currentState = state ?? getStateFromEditor()

    wrapOwnActions(() => {
      if (currentState.title !== originalState.title) {
        lessonEditor.finishEditingTitle(
          modelsNeededForFirstEdit,
          cardStack.id,
          currentState.title,
          originalState.title,
        )
      }
      if (!isEqual(currentState.content.json, originalState.content.json)) {
        lessonEditor.finishEditingUnifiedLesson(
          modelsNeededForFirstEdit,
          cardStack.id,
          currentState.content,
          originalState.content,
          "user",
        )
      }
    })
  }

  const cutVersionRef = useRef<() => void>(() => {})
  const scheduleVersionCut = useDeferredResponse(cutVersionRef)

  const setEditor = useCallback(
    (editor: Editor | null) => {
      const previous = editorRef.current
      if (previous && previous !== editor) clearVersionBoundaryListener(previous)
      editorRef.current = editor
      if (editor) setVersionBoundaryListener(editor, scheduleVersionCut)
      assignRef(propEditorRef, editor)
    },
    [propEditorRef, scheduleVersionCut],
  )

  /**
   * The two baselines below mark where this version began. A save that throws must leave them
   * alone, or the editor counts the words it failed to save as saved.
   */
  const cutVersion = guardedSave(() => {
    if (isAiOpenRef.current) return

    const editState = editStateRef.current
    const editor = editorRef.current
    if (editState.label !== "focused" || !editState.hasEdited || !editor) return

    scheduleIntermediateEdit.cancel()
    const currentState = getStateFromEditor()
    doFinalEdit(currentState)

    // Reset the focused editState.
    editStateRef.current = {
      label: "focused",
      hasEdited: false,
      originalEditorState: currentState,
      lastUpdateEditorState: currentState,
    }
  })
  cutVersionRef.current = cutVersion

  // Edit flow: focusin -> updates (intermediate, throttled) -> focusout (final). Edits
  // outside this flow are handled specially.

  const onFocusIn = () => {
    const state = getStateFromCardStack()
    editStateRef.current = {
      label: "focused",
      hasEdited: false,
      originalEditorState: state,
      lastUpdateEditorState: state,
    }

    setActiveEditorId(cardStack.id)
  }

  const onUpdate = ({ transaction }: EditorEvents["update"]) => {
    // The updater opted out of persistence: the comment action writes the content itself, and
    // the AI panel holds its edits back until the user accepts them.
    if (transaction.getMeta("noUnifiedCardStackAction")) return

    const editState = editStateRef.current
    if (editState.label === "focused") {
      editState.hasEdited = true
      scheduleIntermediateEdit()
    } else {
      // Unfocused local update (toolbar button, Google Classroom modal): issue a final action
      // immediately.
      doFinalEdit()
    }
  }

  /**
   * Saves even when one of our own actions took the focus: the reset below drops hasEdited, so a
   * skipped save loses the last 750 ms of typing. The session closes even when the save throws —
   * the focus is gone either way.
   */
  const onFocusOut = guardedSave(() => {
    const editState = editStateRef.current
    if (editState.label !== "focused") return

    scheduleIntermediateEdit.cancel()
    try {
      if (editState.hasEdited) doFinalEdit()
    } finally {
      editStateRef.current = { label: "unfocused" }
    }

    // We don't unset the activeEditorId because interacting with comments or the BubbleMenu blurs the editor.
  })

  // ----------
  // Reading lesson content
  // ----------

  const isFirstEffectRef = useRef(true)
  useSignalEffect(() => {
    const cardStackState = getStateFromCardStack()

    if (isFirstEffectRef.current) {
      // The editor already loaded this, as the content it was created with. The reads above
      // still have to happen, to subscribe this effect to them.
      isFirstEffectRef.current = false
      return
    }

    // Ignore our own actions, plus concurrent actions while the local user is actively editing
    // or the assistant is holding unaccepted edits in the editor.
    if (
      isInOwnActionRef.current ||
      editStateRef.current.label !== "unfocused" ||
      isAiOpenRef.current
    )
      return

    const editor = editorRef.current
    if (!editor) return

    // TODO: Also send articulated state.
    setEditorState(cardStackState)
  })

  // The read path above stayed shut while the AI panel was open, so anything that arrived
  // meanwhile never landed. Take it now that the panel is gone. Usually there is nothing to
  // take: accepting writes the lesson through an action that updates the local document
  // before this runs, so the editor already holds it — and re-setting the same content would
  // cost the editor its selection for nothing.
  const wasAiOpenRef = useRef(isAiOpen)
  useEffect(() => {
    const hasClosed = wasAiOpenRef.current && !isAiOpen
    wasAiOpenRef.current = isAiOpen
    if (!hasClosed) return

    const editor = editorRef.current
    // As in the read path: the local user's in-progress edit outranks what arrived.
    if (!editor || editStateRef.current.label !== "unfocused") return

    const cardStackState = getStateFromCardStack()
    const editorState = getEditorCardStackState(editor, { isUntitled: isNonInstructional })
    if (
      cardStackState.title === editorState.title &&
      isEqual(cardStackState.content.json, editorState.content.json)
    ) {
      return
    }

    setEditorState(cardStackState)
  }, [isAiOpen])

  // ----------
  // Comments
  // ----------

  const commentActions: CommentActions = useMemo(
    () => ({
      deleteComment: (commentId: string) => {
        const comment = documentStore.findInMemory("comment", commentId)
        if (!comment) return
        lessonEditor.deleteUnifiedCardComment(modelsNeededForFirstEdit, cardStack.id, comment)
      },
      addComment: (cardId: string, cardTitle: string | null, commentText: string) => {
        const editor = editorRef.current
        if (!editor) return

        const cardInfo = findNodeAndPos(
          editor.state.doc,
          (node) => node.type.isInGroup("card") && (node.attrs as CardAttrsBase).id === cardId,
        )
        if (!cardInfo) return

        const commentId = ObjectId.create()

        const cardAttrs = cardInfo.node.attrs as CardAttrsBase
        editor.commands.command(({ tr }) => {
          tr.setNodeAttribute(cardInfo.pos, "commentIds", [...cardAttrs.commentIds, commentId])

          tr.setMeta("addToHistory", false)
          // cardStack.attributes.proseMirror is updated in the comment action below.
          tr.setMeta("noUnifiedCardStackAction", true)
          return true
        })

        lessonEditor.addUnifiedCardComment(
          modelsNeededForFirstEdit,
          planbookId,
          cardStack,
          cardId,
          cardTitle,
          commentId,
          commentText,
          [{ type: "planbook", id: planbookId }],
          [],
          getStateFromEditor().content,
        )
      },
    }),
    [
      documentStore,
      lessonEditor,
      modelsNeededForFirstEdit,
      cardStack,
      planbookId,
      getStateFromEditor,
    ],
  )

  // ----------
  // Editor
  // ----------

  const linkDialog = useLinkDialog()

  const filestack = useEmberService<Filestack>("filestack")
  const analyticsPort = useEditorAnalyticsPort(
    cardStack.id,
    lessonEditIds(modelsNeededForFirstEdit),
  )

  const isFirstRenderRef = useRef(true)
  const isFirstRender = isFirstRenderRef.current
  useEffect(() => {
    isFirstRenderRef.current = false
  }, [])

  // The editor is created once, so the edit-flow handlers reach it through a ref rather than
  // the closures of the render that created it.
  const handlersRef = useRef({ onUpdate, onFocusIn, onFocusOut })
  handlersRef.current = { onUpdate, onFocusIn, onFocusOut }

  const editor = useEditor({
    extensions: cardStackExtensions({
      openLinkDialog: linkDialog.open,
      filestackClient: filestack.client,
      isNonInstructional,
    }),
    editable: canEdit,
    // content is only read on the first render, so we only need to compute it then.
    content: isFirstRender
      ? getEditorJsonContent(getStateFromCardStack(), { isUntitled: isNonInstructional })
      : undefined,
    editorProps: {
      handleDrop(_view, event, slice, moved): boolean {
        if (!canEdit) return false

        // A whole-lesson drag drops on the lesson SLOT, never into an editor. Claim it as
        // handled-and-ignored; the slot's handler sees it regardless.
        if (isLessonDrag(event)) return true

        if (event.dataTransfer && event.dataTransfer.files.length > 0) {
          // The FileUpload and Image extensions handle file drops, not handleSliceDrop.
          return false
        }

        return handleSliceDrop(
          editor,
          event,
          slice,
          moved,
          isNonInstructional ? "untitledCard" : "titledCard",
        )
      },

      handleDOMEvents: {
        focusin() {
          handlersRef.current.onFocusIn()
        },
        focusout() {
          handlersRef.current.onFocusOut()
        },
      },
    },
    onUpdate(updateProps) {
      handlersRef.current.onUpdate(updateProps)
    },
    onCreate({ editor }) {
      if (canEdit || canComment) {
        // Record editor analytics.
        setAnalyticsRuntime(editor, {
          hostId: asHostId(cardStack.id),
          port: analyticsPort,
          onError: reportEditorAnalyticsError,
        })
      }
    },
  })

  useEffect(() => {
    setEditor(editor)
    return () => setEditor(null)
  }, [editor, setEditor])

  // ----------
  // Other UnifiedTiptapRenderer props
  // ----------

  const derivedPermissions: DerivedPermissions = useMemo(
    () => ({ canEdit, canComment }),
    [canEdit, canComment],
  )
  const session = useEmberService<Session>("session")
  const currentUserId = session.userId
  const addFromGoogleDrive = useAddFromGoogleDrive()

  // Everything the editor hands to the UI inside its EditorContent (ReactNodeViews and their
  // descendants — context reaches them through Tiptap's node-view portals). Every field must
  // change rarely: a fresh object here re-renders every node view.
  const ui: Omit<UnifiedTiptapUiValue, "arbiter" | "setCardOverlayOpen"> = useMemo(
    () => ({
      permissions: derivedPermissions,
      isNonInstructional,
      studentLessonUrl,
      currentUserId,
      commentActions,
      openGoogleClassroomSidebar,
      filestackClient: filestack.client,
      openStandardsModal,
      addFromGoogleDrive,
    }),
    [
      derivedPermissions,
      isNonInstructional,
      studentLessonUrl,
      currentUserId,
      commentActions,
      openGoogleClassroomSidebar,
      filestack.client,
      openStandardsModal,
      addFromGoogleDrive,
    ],
  )

  const floatingPortal = useMemo(
    () => document.getElementById("unified-tiptap-floating-portal")!,
    [],
  )

  return (
    <UnifiedTiptapRenderer
      editor={editor}
      unifiedTiptapUiValue={ui}
      linkDialog={linkDialog}
      floatingPortal={floatingPortal}
    />
  )
})
